import type { ReactNode } from 'react';
import { Badge, Th, cx } from '@/components/ui';
import { HelpButton } from './HelpButton';
import { nextSort, sortIndicator, type SortState } from './tableSort';
import {
  HIT_PROBABILITY_HELP,
  INDICATOR_HELP,
  SCORE_HELP,
  type IndicatorKey,
} from './indicatorHelp';
import {
  formatKrwCompact,
  formatKrwPlain,
  formatPercent,
  formatUsdPricePlain,
} from '@/lib/format';
import type { Indicator, RecommendationResult } from './types';
import {
  isUsMarket,
  marketLabel,
  quoteArrow,
  quoteColorClass,
  type QuoteView,
} from './priceDisplay';

/**
 * 단기매매 화면에서 되풀이되는 표시 조각들.
 *
 * `components/ui`(공유)가 아니라 여기 두는 이유
 *   전부 이 도메인의 의미에 묶여 있다. "WIN 은 목표가에 먼저 닿은 것" 같은 규칙은
 *   가계부나 혜택 화면에서 쓸 일이 없다. 공유 영역에 올리면 다른 세션이
 *   이 규칙을 모른 채 가져다 쓰게 된다.
 */

// ─────────────────────────────────────────────
// 채점 결과
// ─────────────────────────────────────────────

const RESULT_LABEL: Record<RecommendationResult, string> = {
  WIN: '목표 도달',
  LOSS: '손절',
  OPEN_EXPIRED: '기간 만료',
};

/**
 * 채점 결과 배지.
 *
 * 색은 한국 시장 관행을 따른다 — 목표 도달은 빨강, 손절은 파랑.
 * (lib/format.ts 의 changeColorClass 와 같은 규칙이다)
 *
 * 기간 만료는 회색이다. 목표에도 손절에도 닿지 않고 15영업일이 지난 것이라
 * 이겼다고도 졌다고도 할 수 없다. 이것을 손실로 세면 승률이 실제보다 낮아진다.
 * (docs/impl/16-journal-evaluation.md)
 */
export function ResultBadge({ result }: { result: RecommendationResult | null }) {
  if (result === null) {
    return <Badge tone="neutral">채점 전</Badge>;
  }
  if (result === 'WIN') {
    return <Badge tone="danger">{RESULT_LABEL.WIN}</Badge>;
  }
  if (result === 'LOSS') {
    return <Badge tone="info">{RESULT_LABEL.LOSS}</Badge>;
  }
  return <Badge tone="neutral">{RESULT_LABEL.OPEN_EXPIRED}</Badge>;
}

// ─────────────────────────────────────────────
// 점수
// ─────────────────────────────────────────────

/**
 * 스코어 막대.
 *
 * 0~100 절대 기준이다. 상대 순위가 아니다 — 같은 55점은 어느 날에 나와도 같은 뜻이다.
 * (docs/impl/15-screener.md 정규화 방식)
 *
 * 55점이 추천 하한선이므로 그 자리에 눈금을 둔다.
 * 막대 길이만 보면 "높은가 낮은가"를 판단할 기준이 없다.
 */
export function ScoreBar({ score }: { score: number }) {
  const clamped = Math.max(0, Math.min(100, score));

  return (
    <div className="flex items-center gap-2">
      <span className="flex w-16 shrink-0 items-center text-sm font-semibold tabular text-fg">
        {score.toFixed(1)}
        <HelpButton help={SCORE_HELP} />
      </span>

      <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-surface-hover">
        <div className="h-full bg-fg-muted" style={{ width: `${clamped}%` }} />
        {/* 추천 하한선(min-score 55). 이 아래는 애초에 추천되지 않는다 */}
        <div className="absolute inset-y-0 w-px bg-fg-subtle" style={{ left: '55%' }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// 과거 도달률
// ─────────────────────────────────────────────

/**
 * 백테스트 도달 빈도.
 *
 * 🔴 "수익 확률" 이라고 쓰지 않는다. 예측이 아니라 과거 빈도다. (CLAUDE.md 1.3절)
 *
 * 🔴 표본 수를 항상 함께 보여준다.
 *   38건의 64% 와 500건의 64% 는 신뢰도가 다르다. 숫자만 크게 띄우면
 *   표본이 몇 건이든 같은 무게로 읽힌다.
 */
export function HitProbability({
  probability,
  samples,
}: {
  probability: number | null;
  samples: number | null;
}) {
  if (probability === null) {
    return (
      <span className="inline-flex items-center text-xs text-fg-subtle">
        과거 표본 없음
        <HelpButton help={HIT_PROBABILITY_HELP} />
      </span>
    );
  }

  // 표본이 적으면 숫자를 흐리게 해서 무게를 낮춘다.
  const 표본부족 = samples !== null && samples < 30;

  return (
    <span className="inline-flex items-baseline gap-1">
      <span
        className={cx(
          'text-sm font-semibold tabular',
          표본부족 ? 'text-fg-subtle' : 'text-fg',
        )}
      >
        {formatPercent(probability)}
      </span>
      {samples !== null && (
        <span className="text-xs text-fg-subtle">표본 {samples}건</span>
      )}
      <HelpButton help={HIT_PROBABILITY_HELP} />
    </span>
  );
}

/**
 * 도달률이 무엇인지 설명하는 각주.
 *
 * 화면마다 반복해서 붙인다. 한 번만 적어두면 나중에 본 사람이 그 문장을 못 본다.
 */
export function ProbabilityDisclaimer() {
  return (
    <p className="text-xs text-fg-subtle">
      과거 도달률은 같은 조건에서 목표가에 먼저 닿았던 빈도입니다. 예측이 아닙니다. 매수
      판단과 실행은 직접 하세요.
    </p>
  );
}

// ─────────────────────────────────────────────
// 목표 / 손절
// ─────────────────────────────────────────────

/**
 * 진입가 대비 목표·손절 폭을 한 줄로.
 *
 * 금액만 보여주면 "3,000원 위" 가 큰 것인지 작은 것인지 알 수 없다.
 * 퍼센트를 함께 붙여야 종목 간 비교가 된다.
 */
export function TargetRange({
  targetPercent,
  stopLossPercent,
}: {
  targetPercent: number;
  stopLossPercent: number;
}) {
  return (
    <span className="inline-flex items-center gap-2 text-xs">
      <span className="text-rise">목표 {formatPercent(targetPercent)}</span>
      <span className="text-fg-subtle">·</span>
      <span className="text-fall">손절 {formatPercent(stopLossPercent)}</span>
    </span>
  );
}

// ─────────────────────────────────────────────
// 지표
// ─────────────────────────────────────────────

/**
 * 계산된 지표 목록.
 *
 * 숫자만 나열하지 않고 한 줄 설명을 붙인다.
 * 몇 달 뒤에 이 화면을 다시 볼 때 "이격도 96 이 좋은 건가" 를 여기서 알 수 있어야 한다.
 * 계산식과 임계값 근거는 docs/impl/14-technical-indicator.md.
 *
 * 여기로 옮긴 이유 (2026-08-16)
 *   추천 상세와 종목 상세 모달이 같은 목록을 보여준다.
 *   두 곳에 각자 두면 임계값 설명이 갈린다 — 한쪽만 고치면
 *   같은 지표를 화면마다 다르게 설명하게 된다. (CLAUDE.md 5.2절)
 *
 * Card 로 감싸지 않는다. 추천 상세는 카드 안에, 모달은 섹션 안에 넣기 때문이다.
 */
export function IndicatorGrid({ indicator }: { indicator: Indicator }) {
  /*
    이름과 한 줄 힌트는 indicatorHelp.ts 가 갖는다. (3.11)

    여기에 따로 적어두면 ? 안의 설명과 갈린다 —
    한쪽만 고쳤을 때 같은 지표를 두 가지로 설명하게 된다.
    이 파일이 정하는 것은 "그 값을 어떻게 숫자로 찍을까" 뿐이다.
  */
  const items: { key: IndicatorKey; value: string }[] = [
    { key: 'atrRatio', value: formatPercent(indicator.atrRatio) },
    {
      key: 'disparity20',
      value: indicator.disparity20 === null ? '-' : indicator.disparity20.toFixed(2),
    },
    {
      key: 'bollingerPb',
      value: indicator.bollingerPb === null ? '-' : indicator.bollingerPb.toFixed(2),
    },
    { key: 'rsi14', value: indicator.rsi14 === null ? '-' : indicator.rsi14.toFixed(1) },
    {
      key: 'rangePosition',
      value: indicator.rangePosition === null ? '-' : indicator.rangePosition.toFixed(2),
    },
    { key: 'avgTradeAmount', value: formatKrwCompact(indicator.avgTradeAmount) },
  ];

  return (
    <dl className="flex flex-col gap-3">
      {items.map((item) => {
        const help = INDICATOR_HELP[item.key];

        return (
          <div key={item.key} className="flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              <dt className="flex items-center text-sm text-fg">
                {help.label}
                <HelpButton help={help} />
              </dt>
              <p className="text-xs text-fg-subtle">{help.hint}</p>
            </div>
            <dd className="shrink-0 text-sm font-semibold tabular text-fg">
              {item.value}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

// ─────────────────────────────────────────────
// 정렬 가능한 열 머리
// ─────────────────────────────────────────────

/**
 * 누르면 오름 → 내림 → 기본 순서로 도는 열 머리. (규칙은 tableSort.nextSort)
 *
 * 저점매수 감시 대상에서 시작했고 섹터 표에도 쓰인다(2026-08-28).
 * 숫자 열 전용이라 항상 오른쪽 정렬이다.
 */
export function SortableTh<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  note,
}: {
  label: string;
  sortKey: K;
  sort: SortState<K>;
  onSort: (next: SortState<K>) => void;
  /**
   * 제목 옆에 붙는 부속. "(종가)" 같은 것이다. (2026-09-03)
   * 버튼 밖에 둔다 - 눌러서 정렬하는 대상은 제목이지 이 표시가 아니다.
   */
  note?: ReactNode;
}) {
  return (
    <Th align="right">
      <button
        type="button"
        onClick={() => onSort(nextSort(sort, sortKey))}
        className="hover:text-fg"
      >
        {label}
        {sortIndicator(sort, sortKey)}
      </button>
      {note}
    </Th>
  );
}

/**
 * 값이 두 줄인 열의 머리. 두 이름이 **각각** 정렬 버튼이고 "/" 로 갈린다.
 * (2026-09-10 "거래량을 클릭해도 정렬이 되어야 해" → 2026-09-12 일반화)
 *
 * 🔴 왜 값을 두 줄로 쌓고 열을 나누지 않나 (2026-09-12 사용자 확정)
 *   "주 사용자는 모바일" 이다. 열을 늘리면 폰에서 표가 가로로 밀리는데,
 *   가로 스크롤은 세로 스크롤과 섞이는 순간 손가락이 어디로 가야 할지 모르게 된다.
 *   값을 세로로 쌓으면 열 수가 그대로라 가로 스크롤 자체가 안 생긴다.
 *
 * 🔴 위·아래를 따로 정렬할 수 있어야 한다
 *   "대비"(금액)와 "등락률"(%)은 정렬 결과가 다르다 - 10만원짜리가 +1,000원(+1%)
 *   오른 것과 1만원짜리가 +500원(+5%) 오른 것의 순서가 뒤집힌다.
 *   현재가·거래량도 마찬가지다. 그래서 이름마다 키를 따로 받는다.
 *
 * 누를 때마다 오름 → 내림 → 기본으로 돈다 (tableSort.nextSort).
 */
export function StackedSortTh<K extends string>({
  topLabel,
  topKey,
  bottomLabel,
  bottomKey,
  sort,
  onSort,
  topNote,
  twoLine = false,
}: {
  topLabel: string;
  topKey: K;
  bottomLabel: string;
  bottomKey: K;
  sort: SortState<K>;
  onSort: (next: SortState<K>) => void;
  /** 위 이름 뒤에 붙는 부속. "(종가)" 같은 것 */
  topNote?: ReactNode;
  /**
   * 제목도 두 줄로 쌓는다. (2026-10-05 사용자 요청 - 주도주 · 섹터 표)
   *
   * Th 는 줄바꿈을 막아(whitespace-nowrap) "현재가 (종가) / 거래량" 이 한 줄로 길게 나와 열 폭을 정했다.
   * 값은 이미 두 줄인데 제목이 한 줄이라 열이 값보다 넓었다. 두 줄로 쌓으면 폭이 긴 쪽 한 줄만큼으로 준다 -
   * 그 자리에 왕복성 열을 넣는다(폰 가로 스크롤을 늘리지 않으려고).
   */
  twoLine?: boolean;
}) {
  if (twoLine) {
    return (
      <Th align="right">
        <span className="block">
          <button
            type="button"
            onClick={() => onSort(nextSort(sort, topKey))}
            className="hover:text-fg"
          >
            {topLabel}
            {sortIndicator(sort, topKey)}
          </button>
          {topNote}
        </span>
        <span className="block">
          <button
            type="button"
            onClick={() => onSort(nextSort(sort, bottomKey))}
            className="font-normal text-fg-subtle hover:text-fg"
          >
            {bottomLabel}
            {sortIndicator(sort, bottomKey)}
          </button>
        </span>
      </Th>
    );
  }
  return (
    <Th align="right">
      <button
        type="button"
        onClick={() => onSort(nextSort(sort, topKey))}
        className="hover:text-fg"
      >
        {topLabel}
        {sortIndicator(sort, topKey)}
      </button>
      {topNote}
      <span className="mx-1 font-normal text-fg-subtle">/</span>
      <button
        type="button"
        onClick={() => onSort(nextSort(sort, bottomKey))}
        className="font-normal text-fg-subtle hover:text-fg"
      >
        {bottomLabel}
        {sortIndicator(sort, bottomKey)}
      </button>
    </Th>
  );
}

/**
 * "현재가 (종가) / 거래량" 열 머리. `StackedSortTh` 의 가장 흔한 쓰임이다.
 *
 * 장이 닫혀 있을 때만 "(종가)" 가 붙는 규칙은 `QuoteHeaderNote` 와 같다.
 */
export function PriceVolumeTh<K extends string>({
  priceKey,
  volumeKey,
  sort,
  onSort,
  stale,
  twoLine = false,
}: {
  priceKey: K;
  volumeKey: K;
  sort: SortState<K>;
  onSort: (next: SortState<K>) => void;
  stale: boolean;
  /** 제목을 두 줄로(StackedSortTh.twoLine) */
  twoLine?: boolean;
}) {
  return (
    <StackedSortTh
      topLabel="현재가"
      topKey={priceKey}
      bottomLabel="거래량"
      bottomKey={volumeKey}
      sort={sort}
      onSort={onSort}
      topNote={stale ? <span className="ml-1 font-normal text-fg-subtle">(종가)</span> : null}
      twoLine={twoLine}
    />
  );
}

/**
 * 왕복성 열 설명 한 줄. 주도주 · 섹터 표 아래에 같은 문장을 쓴다(두 곳에 따로 두면 한쪽만 고쳐진다 - CLAUDE.md 5.2).
 * 기간(60 · 120거래일)은 백엔드 SwingWindowService.SHORT_DAYS · MID_DAYS 와 같다 - 바꾸면 여기도
 */
export const SWING_COLUMN_NOTE =
  '왕복 3개월 / 6개월은 최근 60 · 120거래일 동안 종가가 20일 평균선을 몇 번, 얼마나 크게 넘나들었나를 ' +
  '1년 눈금으로 환산한 값입니다(종목 창의 왕복성과 같은 눈금). 위(3개월)가 아래(6개월)보다 크면 최근 더 출렁였다는 뜻입니다. ' +
  '크다고 좋은 종목이라는 뜻은 아니고, 거래가 적은 종목은 값이 튀기 쉽습니다.';

/**
 * 왕복성 3개월 / 6개월 한 칸. (2026-10-05 사용자 요청 - 주도주 · 섹터 표)
 *
 * 두 값 모두 1년 눈금으로 환산한 왕복성이라 위아래를 바로 견줄 수 있다 - 위(3개월)가 크면 최근 더 출렁인다.
 * 소수 첫째까지만 - 폰 폭을 아끼고, 교차 한 번에 값이 몇씩 움직이는 지표라 둘째 자리는 뜻이 없다.
 * 색을 넣지 않는다 - 빨강 · 파랑은 이 앱에서 가격 방향이다.
 */
export function SwingCell({ threeMonth, sixMonth }: { threeMonth: number | null; sixMonth: number | null }) {
  const fmt = (v: number | null) => (v === null ? '-' : v.toFixed(1));
  return (
    <>
      <span className="tabular text-sm text-fg">{fmt(threeMonth)}</span>
      <span className="block text-[11px] leading-tight text-fg-subtle tabular">{fmt(sixMonth)}</span>
    </>
  );
}

// ─────────────────────────────────────────────
// 시세 (2026-09-02 사용자 요청)
// ─────────────────────────────────────────────

/**
 * 종목 이름 아래 붙는 "거래소 + 종목코드" 한 줄.
 *
 * 🔴 왜 거래소를 적나
 *   코드만 있으면 "005930" 이 국내인지 해외인지 알 수 없다. 미국 종목이
 *   섞이는 화면(관심종목·알림)에서 특히 그렇다. 증권사 앱이 모두 이렇게 한다.
 *
 * 줄바꿈을 막는다 - 좁은 화면에서 거래소와 코드가 갈라지면 읽히지 않는다.
 */
export function StockIdentity({
  market,
  symbol,
}: {
  market: string | null | undefined;
  symbol: string | null | undefined;
}) {
  const label = marketLabel(market);
  if (label === null && !symbol) {
    return null;
  }
  return (
    <span className="block whitespace-nowrap text-xs text-fg-subtle">
      {label !== null && <span>{label}</span>}
      {label !== null && symbol && ' '}
      {symbol && <span className="tabular">{symbol}</span>}
    </span>
  );
}

/**
 * 현재가 · 대비 · 등락률 한 덩어리.
 *
 * 🔴 세 값이 같은 색을 쓴다
 *   증권사 화면과 같다. 상승이면 셋 다 빨강, 하락이면 셋 다 파랑이고
 *   장이 닫혀 있으면 셋 다 회색이다. 하나만 색이 있으면 어느 것이
 *   무엇에 대한 색인지 눈으로 이어지지 않는다.
 *
 * 🔴 단위(원/$)를 값에 붙여 한 덩어리로 둔다
 *   .tabular 이 줄바꿈을 막아 "13,610" 과 "원" 이 갈라지지 않는다.
 *
 * @param stacked 세로로 쌓는다. 카드처럼 가로 폭이 좁은 자리에서 쓴다
 */
export function QuoteCell({
  view,
  stacked = false,
  showPrice = true,
}: {
  view: QuoteView;
  stacked?: boolean;
  /**
   * 현재가를 함께 그릴 것인가.
   *
   * 🔴 표에서 현재가가 별도 열이면 false 를 준다 (2026-09-03 버그 수정)
   *   섹터 표가 [현재가] 열에 가격을 찍고 [대비·등락률] 열에 이것을 통째로
   *   넣어, 같은 값이 한 줄에 두 번 나오고 있었다.
   *   ("182,000원 종가" 옆에 "182,000원 ▲ 7,700원 +4.42%")
   */
  showPrice?: boolean;
}) {
  // 🔴 단위를 안 붙인다 (2026-09-03). 한 열이 통째로 같은 통화라 줄마다 반복하면
  //   자릿수를 읽는 데 방해만 되고, 좁은 화면에서 종목명을 밀어낸다
  const money = isUsMarket(view.market) ? formatUsdPricePlain : formatKrwPlain;
  const tone = quoteColorClass(view);

  if (view.price === null) {
    return <span className="text-xs text-fg-subtle">-</span>;
  }

  // 등락을 계산할 기준이 없으면(신규 상장 등) 대비·등락률 칸이 통째로 빈다
  const hasChange = view.change !== null || view.changeRate !== null;
  if (!showPrice && !hasChange) {
    return <span className="text-xs text-fg-subtle">-</span>;
  }

  return (
    <span
      className={cx(
        'inline-flex items-baseline gap-x-1.5',
        stacked ? 'flex-col items-end gap-y-0.5' : 'flex-wrap justify-end',
      )}
    >
      {showPrice && (
        <span className={cx('tabular text-sm font-semibold', tone)}>{money(view.price)}</span>
      )}
      {/*
        🔴 stacked 면 대비와 등락률도 두 줄로 쌓는다 (2026-09-14 사용자 요청)
          전에는 바깥만 세로(현재가 ↔ 대비 묶음)였고 이 묶음 안은 가로였다.
          표는 현재가를 별도 열로 빼(showPrice=false) 바깥 쌓기가 아무 일도 안 했고,
          폰에서는 폭이 좁아 우연히 줄이 넘어갔을 뿐 PC 에서는 한 줄로 붙어 나왔다.
          열 제목이 "대비 / 등락률" 두 줄이라 값도 두 줄이어야 제목과 맞는다.
      */}
      <span
        className={cx(
          'inline-flex text-xs',
          stacked ? 'flex-col items-end gap-y-0.5' : 'items-baseline gap-1',
          tone,
        )}
      >
        {view.change !== null && (
          <span className="tabular">
            {quoteArrow(view)} {money(Math.abs(view.change))}
          </span>
        )}
        {view.changeRate !== null && (
          <span className="tabular">{formatPercent(view.changeRate)}</span>
        )}
      </span>
    </span>
  );
}

/**
 * 열 제목 "현재가" 에 붙는 마감 표시. (2026-09-03 사용자 요청)
 *
 * 🔴 줄마다 붙이던 것을 제목으로 올렸다
 *   전에는 값 옆에 "종가" 를 찍었다. 한 목록의 종목은 장 상태가 모두 같은데
 *   줄마다 반복하니, 좁은 폰 화면에서 그 두 글자가 종목명을 밀어냈다.
 *   제목에 한 번만 적으면 같은 정보가 자리를 훨씬 덜 먹는다.
 *
 * 장이 열려 있으면 아무것도 그리지 않는다.
 */
export function QuoteHeaderNote({
  stale,
  withVolume = false,
}: {
  stale: boolean;
  /**
   * 이 열이 현재가 아래에 거래량을 함께 그리는가. (2026-09-10 사용자 요청)
   *
   * 🔴 값이 두 개인데 제목이 하나면 아래 숫자가 무엇인지 알 수 없다
   *   현재가 밑에 2,331만이 찍혀 있어도 그것이 거래량인지 거래대금인지
   *   시가총액인지 화면 어디에도 없었다. 제목에 적어 둔다.
   */
  withVolume?: boolean;
}) {
  if (!stale && !withVolume) {
    return null;
  }
  return (
    <span className="ml-1 font-normal text-fg-subtle">
      {stale && '(종가)'}
      {withVolume && (stale ? ' / 거래량' : '/ 거래량')}
    </span>
  );
}
